/**
 * Schedule view model. The list endpoint returns every row, so the screen
 * pages it here. Connector names in the artboard are not on the payload.
 */

export const PAGE_SIZE = 50;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function parseJsonList(value) {
  let raw = value;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = []; }
  }
  return Array.isArray(raw) ? raw : [];
}

export function chosenWeekdays(value) {
  const list = parseJsonList(value).map(Number).filter((n) => n >= 1 && n <= 7);
  return [...new Set(list)].sort((a, b) => a - b);
}

export function weekdaysOf(value) {
  const list = chosenWeekdays(value);
  return list.length ? list : [1];
}

export function recipientsOf(value) {
  const out = [];
  const seen = new Set();
  for (const item of parseJsonList(value)) {
    const email = String(item || '').trim().toLowerCase();
    if (!email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

export function clockOf(value) {
  const match = String(value || '06:00').match(/^(\d{1,2}):(\d{2})/);
  let hour = match ? parseInt(match[1], 10) : 6;
  let minute = match ? parseInt(match[2], 10) : 0;
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) hour = 6;
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) minute = 0;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function monthDayOf(value) {
  const text = String(value || '1');
  if (text === '15' || text === 'last') return text;
  return '1';
}

export function cadenceWord(cadence) {
  if (cadence === 'daily') return 'Daily';
  if (cadence === 'monthly') return 'Monthly';
  return 'Weekly';
}

export function destinationWord(destination) {
  if (destination === 'email') return 'Email';
  if (destination === 'knowledge_base') return 'Document';
  return 'Email + document';
}

export function tagsFor(row) {
  const tags = [];
  if (row.cadence === 'weekly') {
    tags.push(weekdaysOf(row.weekdays).map((day) => DAYS[day - 1]).join(' · '));
  } else if (row.cadence === 'monthly') {
    const day = monthDayOf(row.month_day);
    tags.push(day === 'last' ? 'Last day' : day === '15' ? '15th' : '1st');
  }
  tags.push(destinationWord(row.destination));
  return tags;
}

export function errorText(value) {
  const raw = String(value || '').replace(/^Skipped:\s*/i, '').trim();
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function accountTz(me) {
  if (me && me.timezone) return String(me.timezone);
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function tzLabel(tz) {
  const name = tz || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: name,
      timeZoneName: 'long',
      hour: 'numeric',
    }).formatToParts(new Date());
    const found = parts.find((part) => part.type === 'timeZoneName');
    return (found && found.value) || name.replace(/_/g, ' ');
  } catch {
    return String(name).replace(/_/g, ' ');
  }
}

export function clip(text, max) {
  const clean = String(text || '').trim().replace(/\s+/g, ' ');
  if (clean.length <= max) return clean;
  return `${clean.slice(0, Math.max(0, max - 1))}…`;
}

export function savedPrompts(rows) {
  const out = [];
  const seen = new Set();
  for (const row of rows || []) {
    const prompt = String(row.prompt || '').trim();
    if (!prompt || seen.has(prompt.toLowerCase())) continue;
    seen.add(prompt.toLowerCase());
    out.push(prompt);
    if (out.length >= 20) break;
  }
  return out;
}

export function pageOf(rows, shown) {
  const list = Array.isArray(rows) ? rows : [];
  const count = Math.min(list.length, Math.max(PAGE_SIZE, Number(shown) || PAGE_SIZE));
  return {
    rows: list.slice(0, count),
    more: list.length > count,
    remaining: list.length - count,
  };
}

export function lastRunPhrase(value, now = Date.now()) {
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return ago(Math.floor(secs / 60), 'minute');
  if (secs < 86400) return `about ${ago(Math.floor(secs / 3600), 'hour')}`;
  return ago(Math.floor(secs / 86400), 'day');
}

function ago(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'} ago`;
}

function zonedParts(iso, tz) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  let fmt;
  try {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      weekday: 'short',
    });
  } catch {
    return null;
  }
  const bag = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  return bag;
}

export function nextRunLabel(row, now = Date.now()) {
  const fallback = clockOf(row && row.run_time);
  if (!row || !row.next_run_at) return fallback;
  const t = new Date(row.next_run_at).getTime();
  if (Number.isNaN(t)) return fallback;
  const parts = zonedParts(row.next_run_at, row.timezone || 'UTC');
  if (!parts || parts.hour == null) return fallback;
  const clock = `${String(parts.hour).padStart(2, '0')}:${String(parts.minute || '0').padStart(2, '0')}`;
  const soon = t - now < 24 * 60 * 60 * 1000;
  if (soon || !parts.weekday) return clock;
  return `${parts.weekday} ${clock}`;
}

export function statusLine(row, now = Date.now()) {
  if (!row || row.enabled === false) return 'Paused · resumes when re-enabled';
  const last = row.last_run_at ? lastRunPhrase(row.last_run_at, now) : '';
  const next = nextRunLabel(row, now);
  if (last && next) return `Last ran ${last} · next ${next}`;
  if (last) return `Last ran ${last}`;
  if (next) return `Active · next run ${next}`;
  return 'Active';
}

export function blankForm(me) {
  const email = String((me && me.email) || '').trim().toLowerCase();
  return {
    id: '',
    prompt: '',
    cadence: 'daily',
    run_time: '06:00',
    weekdays: [1],
    month_day: '1',
    destination: 'both',
    recipients: email.includes('@') ? [email] : [],
    timezone: accountTz(me),
    save_question: true,
    enabled: true,
  };
}

export function formFromRow(row, me) {
  const cadence = ['daily', 'weekly', 'monthly'].includes(row.cadence) ? row.cadence : 'weekly';
  const destination = ['email', 'knowledge_base', 'both'].includes(row.destination) ? row.destination : 'both';
  return {
    id: row.id,
    prompt: String(row.prompt || ''),
    cadence,
    run_time: clockOf(row.run_time),
    weekdays: weekdaysOf(row.weekdays),
    month_day: monthDayOf(row.month_day),
    destination,
    recipients: recipientsOf(row.recipients),
    timezone: row.timezone || accountTz(me),
    save_question: false,
    enabled: row.enabled !== false,
  };
}

export function payloadFromForm(form, productId) {
  const prompt = String((form && form.prompt) || '').trim();
  if (!prompt) return { error: 'Write a question first' };
  const destination = form.destination;
  const emailOn = destination === 'email' || destination === 'both';
  if (emailOn && !recipientsOf(form.recipients).length) return { error: 'Add at least one email recipient' };
  if (!emailOn && destination !== 'knowledge_base') return { error: 'Choose Email or Documents' };
  const weekdays = chosenWeekdays(form.weekdays);
  if (form.cadence === 'weekly' && !weekdays.length) return { error: 'Pick at least one weekday' };
  return {
    body: {
      product_id: productId,
      prompt,
      cadence: form.cadence || 'daily',
      destination: destination || 'both',
      run_time: clockOf(form.run_time),
      weekdays: weekdays.length ? weekdays : weekdaysOf(form.weekdays),
      month_day: monthDayOf(form.month_day),
      recipients: recipientsOf(form.recipients),
      timezone: form.timezone || 'UTC',
      enabled: form.enabled !== false,
      save_question: !!form.save_question,
    },
  };
}

const CADENCES = ['daily', 'weekly', 'monthly'];

export function cadenceRadios(current) {
  const value = CADENCES.includes(current) ? current : 'daily';
  return CADENCES.map((item) => ({ value: item, checked: item === value }));
}

export function weekdayToggles(days) {
  const selected = new Set(weekdaysOf(days));
  return [1, 2, 3, 4, 5, 6, 7].map((iso) => ({ iso, pressed: selected.has(iso) }));
}

export function destOn(destination, key) {
  if (key === 'email') return destination === 'email' || destination === 'both';
  return destination === 'knowledge_base' || destination === 'both';
}

export function destinationOf(email, docs) {
  if (email && docs) return 'both';
  if (email) return 'email';
  if (docs) return 'knowledge_base';
  return 'none';
}

export function toggleDestination(current, key) {
  const email = key === 'email' ? !destOn(current, 'email') : destOn(current, 'email');
  const docs = key === 'kb' ? !destOn(current, 'kb') : destOn(current, 'kb');
  return { destination: destinationOf(email, docs) };
}

export function toggleWeekday(days, iso) {
  const set = new Set(chosenWeekdays(days));
  if (set.has(iso)) {
    if (set.size === 1) return { error: 'Pick at least one weekday', weekdays: [...set] };
    set.delete(iso);
  } else set.add(iso);
  return { weekdays: [...set].sort((a, b) => a - b) };
}

export function addRecipient(list, email) {
  const value = String(email || '').trim().toLowerCase();
  if (!value.includes('@')) return { error: 'Enter a valid email address', recipients: recipientsOf(list) };
  const recipients = recipientsOf(list);
  if (!recipients.includes(value)) recipients.push(value);
  return { recipients };
}

export function removeRecipient(list, email, destination) {
  const recipients = recipientsOf(list).filter((item) => item !== email);
  const needsEmail = destination === 'email' || destination === 'both';
  if (needsEmail && !recipients.length) {
    return { error: 'Keep at least one recipient, or turn off email', recipients: recipientsOf(list) };
  }
  return { recipients };
}

export function runToast(result, destination) {
  const warnings = result && result.run && Array.isArray(result.run.warnings) ? result.run.warnings : [];
  let where = 'Check email and Documents.';
  if (destination === 'email') where = 'Check email.';
  if (destination === 'knowledge_base') where = 'Check Documents.';
  if (warnings.length) return `${where} ${warnings.join(' ')}`;
  return `Report ready. ${where}`;
}
